<template>
  <div
    class="irp-recommended-partners-table"
    data-testid="irp-recommended-partners-table"
  >
    <div class="irp-recommended-partners-table__header">
      <h2 class="irp-recommended-partners-table__title">
        Recommended Partners
      </h2>
      <BittsTooltip
        v-if="hasPartnerScores"
        placement="right"
        :mount-to-body="true"
        overlay-class="max-w-[240px] text-left"
      >
        <button
          type="button"
          class="irp-recommended-partners-table__info"
          :aria-label="description"
          data-testid="irp-recommended-partners-info"
        >
          <FontAwesomeIcon
            :icon="['fak', 'information-circle']"
            class="irp-recommended-partners-table__icon"
          />
        </button>
        <template #title>{{ description }}</template>
      </BittsTooltip>
    </div>
    <BittsEmptyState
      v-if="hasError"
      :font-awesome-icon="ERROR_STATE.icon"
      :title="ERROR_STATE.title"
      :description="ERROR_STATE.description"
      :button="RETRY_BUTTON"
      data-testid="irp-recommended-partners-error-state"
      @button-clicked="refetch"
    />
    <BittsEmptyState
      v-else-if="isEmpty"
      :font-awesome-icon="EMPTY_STATE.icon"
      :title="EMPTY_STATE.title"
      :description="EMPTY_STATE.description"
      data-testid="irp-recommended-partners-empty-state"
    />
    <div v-else class="irp-recommended-partners-table__body">
      <IRPRecommendedPartnersTableSkeleton v-if="!isGridVisible" />
      <BittsTablePro
        :class="{
          'irp-recommended-partners-table__grid--rendering': !isGridVisible,
        }"
        :rows="partners"
        :columns="columnDefinitions"
        :default-column-def="DEFAULT_COLUMN_DEF"
        :row-height="56"
        :zebra="true"
        :pagination="false"
        :suppress-movable-columns="true"
        @first-data-rendered="markFirstDataRendered"
      />
    </div>
    <RouterLink
      v-if="hasPartners"
      class="irp-recommended-partners-table__view-all"
      :to="{ name: IRP_TAB_ROUTES[IRP_TAB.details], params: recordParams }"
    >
      <BittsButton
        text="View all partners"
        type="neutral"
        variant="outline"
        size="x-small"
        data-testid="irp-recommended-partners-view-all"
      />
    </RouterLink>
  </div>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsEmptyState,
  BittsTablePro,
  BittsTooltip,
} from '@crossbeam/bitts';
import type { BittsIcon } from '@crossbeam/bitts';

import type { ColDef } from '@ag-grid-community/core';
import { computed, ref } from 'vue';

import { PARTNER_SCORES } from '@/constants/partners';
import { defaultErrorMessage } from '@/errors';
import { useIndividualRecordStore, usePartnersStore } from '@/stores';
import type { RecordOverviewPartnerType } from '@/types/individual_record';

import { IRP_TAB, IRP_TAB_ROUTES } from './irp';
import IRPRecommendedPartnersTableSkeleton from './IRPRecommendedPartnersTableSkeleton.vue';
import { useRecommendedPartnersColumnDefinitions } from './useRecommendedPartnersColumnDefinitions';
import { useRecordRouteParams } from './useRecordRouteParams';

const DEFAULT_COLUMN_DEF: ColDef = {
  sortable: false,
  resizable: false,
  wrapHeaderText: true,
  autoHeaderHeight: true,
};

const RECOMMENDATION_REASON =
  'based on their Crossbeam partner score, so you can focus on where you’ll see the most impact.';

const EMPTY_STATE: { title: string; description: string; icon: BittsIcon } = {
  title: 'No recommended partners',
  description: 'No partner has shared an overlapping record for this account.',
  icon: ['fak', 'partner'],
};

const ERROR_STATE: { title: string; description: string; icon: BittsIcon } = {
  title: 'Recommended partners could not load',
  description: defaultErrorMessage,
  icon: ['fak', 'warning'],
};

const RETRY_BUTTON = {
  text: 'Try again',
  type: 'neutral',
  variant: 'outline',
} as const;

const individualRecordStore = useIndividualRecordStore();
const partnersStore = usePartnersStore();

const columnDefinitions = useRecommendedPartnersColumnDefinitions();
const recordParams = useRecordRouteParams();

const section = computed(() => individualRecordStore.all);
const partners = computed(() => section.value.partners.items);
const isEmpty = computed(
  () => !section.value.isLoading && !partners.value.length,
);

const hasFirstDataRendered = ref(false);
const isGridVisible = computed(
  () => !section.value.isLoading && hasFirstDataRendered.value,
);

const hasError = computed(() => !!section.value.error);
const hasPartners = computed(() => !hasError.value && !!partners.value.length);
const hasPartnerScores = computed(
  () => hasPartners.value && partners.value.some(hasPartnerScore),
);
const description = computed(() =>
  partners.value.length === 1
    ? `This partner is recommended ${RECOMMENDATION_REASON}`
    : `These ${partners.value.length} partners are recommended ${RECOMMENDATION_REASON}`,
);

function hasPartnerScore({
  partner_organization_uuid,
}: RecordOverviewPartnerType) {
  const scoreBucket =
    partnersStore.getPartnerOrgByUuid(partner_organization_uuid)?.metrics
      ?.partner_score_bucket ?? PARTNER_SCORES.NONE;
  return (
    scoreBucket !== PARTNER_SCORES.NONE &&
    scoreBucket !== PARTNER_SCORES.UNKNOWN
  );
}

function markFirstDataRendered() {
  hasFirstDataRendered.value = true;
}

async function refetch() {
  await individualRecordStore.fetchSection('all', { isForced: true });
}
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.irp-recommended-partners-table {
  @apply flex flex-col gap-16;
}

.irp-recommended-partners-table__body {
  @apply relative;
}

.irp-recommended-partners-table__grid--rendering {
  @apply invisible absolute inset-x-0 top-0;
}

.irp-recommended-partners-table__header {
  @apply flex items-center gap-6;
}

.irp-recommended-partners-table__view-all {
  @apply self-start;
}

.irp-recommended-partners-table__title {
  @apply text-lg font-bold text-neutral-text-strong;
}

.irp-recommended-partners-table__info {
  @apply flex shrink-0 items-center appearance-none border-0 bg-transparent p-0 cursor-default;
}

.irp-recommended-partners-table__info:focus-visible {
  @apply outline-2 outline-offset-2 outline-neutral-border-focus rounded-4;
}

.irp-recommended-partners-table__icon {
  @apply h-12 w-12 text-neutral-accent;
}
</style>
